import { createContext, useState, useEffect, useRef, useCallback } from "react";
import {
    createStaffUser,
    createUserDocumentFromAuth,
    errorToast,
    getUsersDocuments,
    onAuthStateChangeListener,
    signOutUser,
    successToast,
    updateUserDoc,
} from "../utils/firebase/firebase.utils";
import { isAdminProfile } from "../utils/firebase/auth.utils";

export const UserContext = createContext({
    currentUser: null,
    setCurrentUser: () => null,
    authReady: false,
    users: [],
    getUsers: () => {},
    addStaffUser: () => {},
    updateStaffUser: () => {},
});

export const UserProvider = ({ children }) => {
    const [currentUser, setCurrentUser] = useState(null);
    const [authReady, setAuthReady] = useState(false);
    const [users, setUsers] = useState([]);
    const currentUserRef = useRef(null);
    // Keep ref in sync during render so child effects never see a stale null.
    currentUserRef.current = currentUser;

    useEffect(() => {
        const unsubscribe = onAuthStateChangeListener(async (user) => {
            try {
                if (user) {
                    const profile = await createUserDocumentFromAuth(user);

                    if (!profile || profile.del === 'yes') {
                        errorToast('Unable to login. Contact Administrator');
                        await signOutUser();
                        setCurrentUser(null);
                        localStorage.setItem('curUser', '');
                        return;
                    }

                    if (!isAdminProfile(profile)) {
                        errorToast('Admin access only. Ask an administrator to activate your account.');
                        await signOutUser();
                        setCurrentUser(null);
                        localStorage.setItem('curUser', '');
                        return;
                    }

                    setCurrentUser(profile);
                    localStorage.setItem('curUser', JSON.stringify(profile));
                } else {
                    setCurrentUser(null);
                    localStorage.setItem('curUser', '');
                }
            } catch (error) {
                console.log('Auth state error: ', error);
                setCurrentUser(null);
                localStorage.setItem('curUser', '');
            } finally {
                setAuthReady(true);
            }
        });

        return unsubscribe;
    }, []);

    const getUsers = useCallback(async () => {
        if (!isAdminProfile(currentUserRef.current)) {
            setUsers([]);
            return [];
        }
        const fetchUsers = await getUsersDocuments();
        const list = Array.isArray(fetchUsers) ? fetchUsers : [];
        setUsers(list);
        return list;
    }, []);

    const addStaffUser = async (payload, msg = 'Staff user created.') => {
        await createStaffUser(payload);
        await getUsers();
        successToast(msg);
    }

    const updateStaffUser = async (userDoc, msg = 'Staff user updated.') => {
        await updateUserDoc(userDoc);
        await getUsers();
        if (currentUser?.uid && (userDoc.uid === currentUser.uid || userDoc.id === currentUser.uid)) {
            const next = {
                ...currentUser,
                ...userDoc,
                uid: currentUser.uid,
            };
            setCurrentUser(next);
            localStorage.setItem('curUser', JSON.stringify(next));
        }
        successToast(msg);
    }

    useEffect(() => {
        if (!isAdminProfile(currentUser)) {
            setUsers([]);
            return undefined;
        }

        let cancelled = false;
        getUsers()
            .then((list) => {
                if (cancelled) return;
                setUsers(Array.isArray(list) ? list : []);
            })
            .catch((error) => {
                if (cancelled) return;
                console.error('Load staff users failed:', error);
            });

        return () => {
            cancelled = true;
        };
    }, [currentUser, getUsers]);

    const value = {
        currentUser,
        setCurrentUser,
        authReady,
        users,
        getUsers,
        addStaffUser,
        updateStaffUser,
    };

    return <UserContext.Provider value={value}>{children}</UserContext.Provider>
};
